import { DEV } from "solid-js"
import { Color, DoubleSide, MeshLambertMaterial } from "three"
import type { Engine } from "../../../../core/engine/Engine.type"
import { getWaterNormals1024Texture } from "../textures/getWaterNormals1024TexturePromise"
import { waterPlaneV7LambertV2FragmentShader, waterPlaneV7LambertV2VertexShader } from "./LambertShaderV2/waterPlaneV7LambertShaders"

// Global time source for seamless tiling across multiple water planes
let globalStartTime: number = Date.now()
const globalAnimationCallbacks = new Set<() => void>()
let globalAnimationId: number | null = null

function getGlobalWaterTime(): number {
  return (Date.now() - globalStartTime) * 0.0001
}

function startGlobalWaterAnimation() {
  if (globalAnimationId !== null) return
  
  const updateGlobalTime = () => {
    globalAnimationCallbacks.forEach(callback => callback())
    globalAnimationId = requestAnimationFrame(updateGlobalTime)
  }
  globalAnimationId = requestAnimationFrame(updateGlobalTime)
}

function addGlobalWaterAnimationCallback(callback: () => void) {
  globalAnimationCallbacks.add(callback)
  startGlobalWaterAnimation()
  
  return () => {
    globalAnimationCallbacks.delete(callback)
    if (globalAnimationCallbacks.size === 0 && globalAnimationId !== null) {
      cancelAnimationFrame(globalAnimationId)
      globalAnimationId = null
    }
  }
}

const speed: number = 2.0

export default function createWaterPlaneV7LambertWithShaderV2Material(
  engine: Engine,
): MeshLambertMaterial {

  const material = new MeshLambertMaterial({
    // vertexColors: true,
    color: new Color(0xaaaaff),
    // opacity: 0.2,
    opacity: 0.5,
    // opacity: 1.0,
    transparent: true,
  })
  if (DEV) {
    material.side = DoubleSide
    // material.wireframe = true
  }

  material.onBeforeCompile = (shader) => {
    // Add custom uniforms for water normals
    shader.uniforms.normalSampler = { value: getWaterNormals1024Texture() }
    shader.uniforms.time = { value: 0.0 }
    shader.uniforms.size = { value: 1.0 }
    shader.uniforms.speed = { value: speed }
    
    shader.vertexShader = waterPlaneV7LambertV2VertexShader
    shader.fragmentShader = waterPlaneV7LambertV2FragmentShader
    
    // Use global time source for seamless tiling across multiple water planes
    const updateTime = () => {
      shader.uniforms.time.value = getGlobalWaterTime() * speed
    }
    
    // Add to global animation callbacks
    const removeCallback = addGlobalWaterAnimationCallback(updateTime)
    
    // Store cleanup function for potential cleanup
    ;(material as any)._waterAnimationCleanup = removeCallback
  }

  // Add cleanup method to material
  const originalDispose = material.dispose.bind(material)
  material.dispose = () => {
    const cleanup = (material as any)._waterAnimationCleanup
    if (cleanup) {
      cleanup()
      ;(material as any)._waterAnimationCleanup = null
    }
    originalDispose()
  }

  return material
}